import React from "react";
import { Link } from "react-router-dom";
import { LEGAL } from "../constants/legal";
import GrievanceOfficer from "./GrievanceOfficer";

// The Privacy Policy (DPDP Act 2023 + Rules 2025, IT Act/Rules, GDPR/UK GDPR, CCPA/CPRA,
// CalOPPA). Kept in English as the authoritative text; it must describe what the code
// actually does — update it whenever data collection or a service provider changes.

const PROVIDERS = [
  ["Render (USA)", "Hosts the SaatSaheli application."],
  ["Neon (USA)", "Hosts our database: accounts, content, messages, orders and payment records."],
  ["Cloudflare (global)", "Domain/DNS and content delivery; Cloudflare R2 stores the images, audio and documents you upload; Cloudflare Web Analytics counts page views without cookies or personal data."],
  ["Stripe (global)", "Processes all payments, subscriptions and refunds. Stripe receives your payment details directly; we never see or store card numbers."],
  ["Google", "Google Sign-In (only if you choose it), reCAPTCHA on sign-up and public forms to block spam, Google Fonts for page typography, and Gmail to send our emails."],
  ["Hugging Face", "Generates images when you use the AI image tool; it receives only the text prompt you type."],
];

export default function PrivacyNotice() {
  return (
    <>
      <h2>Privacy Policy</h2>
      <p>
        This policy explains what personal data {LEGAL.operator} collects through {LEGAL.site}, why, who receives
        it, how long we keep it, and the rights you have. {LEGAL.operator} is the data fiduciary (data controller)
        for your personal data. Effective: {LEGAL.effectiveDate}.
      </p>

      <h3>1. Data we collect</h3>
      <ul>
        <li><strong>Account details:</strong> name, email, age, optional phone number, and optional profile details (display name, public handle, bio, interests, location, profile photo). Your password is stored only as a secure hash; if you use Google Sign-In we receive your verified Google email and name.</li>
        <li><strong>Content you create:</strong> books, magazines, articles, recipes, podcasts, galleries, images, audio and documents you upload, comments, likes and favourites. Content you publish is public.</li>
        <li><strong>Messages:</strong> private messages about items marked For Sale, contact-form and enquiry messages. These are visible to the people in the conversation and to SaatSaheli administrators, who may review them for safety and to handle complaints.</li>
        <li><strong>Payments:</strong> for donations, sponsorships and subscriptions we keep the amount, currency, status, your name and email, Stripe reference numbers, and the IP address and browser type used at checkout (for fraud prevention and our records). We never receive your full card number.</li>
        <li><strong>Shop orders:</strong> the delivery name, address and phone number you enter, and your order history.</li>
        <li><strong>Usage:</strong> pages viewed, the referring page, and device and browser type. If you are signed in, visits are linked to your account (for security and support). If you are not, you are counted with an anonymous code that changes every day; we do not store your IP address for this.</li>
        <li><strong>Emails we send you:</strong> a log of each email (recipient, subject, time, delivery status) and an archive copy in our mailbox.</li>
        <li><strong>Security records:</strong> sign-in times and account status.</li>
      </ul>

      <h3>2. Why we use it, and our legal basis</h3>
      <ul>
        <li><strong>To provide the service you signed up for</strong> (account, publishing, messages, payments, orders) — performance of our contract with you.</li>
        <li><strong>With your consent</strong> — for example agreeing to automatic renewal of a subscription. You can withdraw consent at any time; this does not affect anything done before.</li>
        <li><strong>Legitimate interests</strong> — keeping accounts and payments secure, preventing fraud and spam, and understanding overall site usage to improve it.</li>
        <li><strong>Legal obligations</strong> — keeping payment and tax records, responding to lawful requests and complaints.</li>
      </ul>
      <p>We do not sell your personal data, share it for advertising, or use it to build advertising profiles. We do not make automated decisions with legal effects about you.</p>

      <h3>3. Who receives your data</h3>
      <p>Only these service providers, each receiving the minimum needed for its task:</p>
      <ul>
        {PROVIDERS.map(([name, desc]) => (
          <li key={name}><strong>{name}:</strong> {desc}</li>
        ))}
      </ul>
      <p>We may also disclose data when required by law or a valid order from a court or government authority, or to protect the safety of users.</p>

      <h3>4. Where your data is stored</h3>
      <p>
        Our servers and database are in the United States. If you are in India, the EU/UK or elsewhere, your data is
        transferred there. We use providers bound by contractual data-protection commitments (including Standard
        Contractual Clauses where required), and transfers comply with India's DPDP Act.
      </p>

      <h3>5. How long we keep it</h3>
      <p>
        Your personal data stays in our database and file storage until it is deleted. You can remove content you
        created yourself, and you can ask us to delete your account and data at any time (see "Your rights" below);
        we delete it within 30 days of confirming the request, except records the law requires us to keep.
      </p>

      <h3>6. Cookies and browser storage</h3>
      <p>
        We do not use advertising or analytics cookies. Our page-view statistics (Cloudflare Web Analytics and our own
        anonymous counter) work without cookies. We also record whether a page was opened in the installed app, and
        when the app is installed. We only store what the site needs to work: your sign-in token and
        session timer, language and region choice, likes and favourites saved on your device if you are not signed
        in, and whether you dismissed the "install app" prompt. Google sets its own cookies only when you use Google
        Sign-In or complete a reCAPTCHA. Because we do no cross-site tracking, browser "Do Not Track" and Global
        Privacy Control signals need no action from us; we never sell or share your data.
      </p>

      <h3>7. Your rights</h3>
      <p>Wherever you live, you can ask us to:</p>
      <ul>
        <li>tell you what personal data we hold about you and how we use it, and give you a copy;</li>
        <li>correct or complete inaccurate data (you can edit most of it yourself in Account and Profile);</li>
        <li>delete your account and data (except records the law requires us to keep);</li>
        <li>withdraw a consent you gave, or object to processing based on our legitimate interests;</li>
        <li>in India, nominate another person to exercise your rights if you die or become incapacitated.</li>
      </ul>
      <p>
        <strong>California residents</strong> also have the right to know, delete and correct, and to opt out of the
        "sale" or "sharing" of personal information. We do not sell or share personal information, and we will not
        discriminate against you for using your rights. You may use an authorized agent.
      </p>
      <p>
        To make a request, email <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a> from your
        registered email address. We will reply within 30 days (45 days for California requests). If you are not
        satisfied, you can raise a complaint with our {LEGAL.grievanceOfficer.title.toLowerCase()} (below), and then to the Data Protection Board of India,
        your EU/UK data protection authority, or the California Privacy Protection Agency.
      </p>

      <h3>8. Children</h3>
      <p>
        SaatSaheli is for adults aged 18 and over. We do not knowingly collect personal data from anyone under 18; if
        we learn that we have, we delete it. See our <Link to="/policies#terms">Terms of Use</Link>.
      </p>

      <h3>9. Security and breaches</h3>
      <p>
        We use HTTPS, hashed passwords, signed sign-in tokens, verified payment webhooks and administrator-only access
        controls. No system is perfectly secure; if a personal data breach affects you, we will tell you and the
        relevant authorities without undue delay, as the law requires.
      </p>

      <h3>10. Changes</h3>
      <p>We may update this policy. The effective date above changes when we do, and we will notify registered users of significant changes.</p>

      <GrievanceOfficer />
    </>
  );
}
